/**
 * Bottom-left meta strip — keybindings followed by the per-edit token
 * cost (so the user sees "X tokens · Y left after" inline with the
 * shortcut hints instead of stacked above the dock).
 */
import { computeTokenCost } from '@agentbrush/shared';
import { useEditorStore } from '../../../stores/editor.js';

interface HintItem {
  keys: string[];
  label: string;
}

const HINTS: HintItem[] = [
  { keys: ['B'], label: 'brush' },
  { keys: ['E'], label: 'erase' },
  { keys: ['Z'], label: 'undo' },
  { keys: ['Space'], label: 'pan' },
];

interface KeyboardHintProps {
  /** User's current balance. When omitted, the "left after" hint is hidden. */
  remainingTokens?: number | null;
}

export function KeyboardHint({ remainingTokens = null }: KeyboardHintProps = {}) {
  const quality = useEditorStore((s) => s.quality);
  const cost = computeTokenCost({ quality, size: '1024x1024' });
  const remainingAfter = remainingTokens !== null ? Math.max(0, remainingTokens - cost) : null;
  const insufficient = remainingTokens !== null && remainingTokens < cost;

  return (
    <div
      aria-hidden="true"
      data-testid="keyboard-hint"
      style={{
        position: 'absolute',
        bottom: '76px',
        left: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontFamily: 'var(--font-mono)',
        fontSize: '11px',
        color: 'var(--color-text-muted)',
        zIndex: 19,
        pointerEvents: 'none',
        // Drop shadow so the strip stays legible over busy canvas content.
        // text-shadow rather than filter:drop-shadow because the strip is
        // mostly text + tiny kbd boxes — a heavier filter blurs the kbd
        // borders and reads as a halo around each character instead of a
        // shadow.
        textShadow: '0 1px 4px rgba(0, 0, 0, 0.85), 0 0 12px rgba(0, 0, 0, 0.45)',
      }}
    >
      {HINTS.map((hint, i) => (
        <span key={hint.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {i > 0 && <span style={{ color: 'var(--color-border-bright)' }}>·</span>}
          {hint.keys.map((k) => (
            <kbd
              key={k}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '18px',
                height: '18px',
                padding: '0 6px',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
                fontFamily: 'var(--font-mono)',
                fontSize: '10px',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                lineHeight: 1,
                textShadow: 'none',
              }}
            >
              {k}
            </kbd>
          ))}
          {hint.label}
        </span>
      ))}

      <span style={{ color: 'var(--color-border-bright)' }}>·</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <b
          style={{
            color: insufficient ? 'var(--color-accent-red, #ef4444)' : 'var(--color-text-primary)',
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {cost}
        </b>
        tokens
      </span>
      {remainingAfter !== null && (
        <>
          <span style={{ color: 'var(--color-border-bright)' }}>·</span>
          <span>{remainingAfter} left after</span>
        </>
      )}
    </div>
  );
}
